import "./App.css";
import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import Contact from "./Contact"; // Import the Contact component
import Blogs from "./Blogs";
import Faq from "./Faq";
import LogoMark from "./LogoMark";

// Condensed primary links shown inline on desktop (mirrors Grove)
const desktopNav = [
  { label: "Welcome", url: "#welcome" },
  { label: "About", url: "#about-me" },
  { label: "Services", url: "#services" },
  { label: "Blogs", url: "#blogs" },
  { label: "FAQ", url: "#faq" },
];

// Full list shown in the mobile drop-down
const mobileNav = [
  { label: "Welcome", url: "#welcome" },
  { label: "About me", url: "#about-me" },
  { label: "What to expect", url: "#what-to-expect" },
  { label: "Why counselling", url: "#why" },
  { label: "My services", url: "#services" },
  { label: "I can help with", url: "#help" },
  { label: "Blogs", url: "#blogs" },
  { label: "Testimonials", url: "#testimonials" },
  { label: "FAQ", url: "#faq" },
  { label: "Contact me", url: "#contact" },
];

// Fixed header overlays content, so anchored scrolls offset by its height
const HEADER_OFFSET = 76;

// How long the arrival scroller keeps watching the hash target after page
// load. Wall-clock, not frame-count: rAF cadence varies under load, and the
// window must outlast a serverless cold start on the blogs fetch,
// whose late mount shifts everything below it down the page.
const HASH_WATCH_MS = 12_000;

const areas_of_support = [
  "Abuse",
  "Addiction",
  "ADHD",
  "Anxiety",
  "Attachment Disorder",
  "Hoarding Disorder",
  "Bereavement",
  "OCD/Rumination",
  "Loneliness",
  "Low Self-Confidence",
  "Low Self-Esteem",
  "Work Related Issues",
  "Panic Attacks",
  "Physical Abuse",
  "Bullying",
  "Depression",
  "Disabilities",
  "Emotional Abuse",
  "Family Issues",
  "Feeling Sad",
  "Generalised Anxiety Disorder",
  "Burnout",
  "Communication",
  "Redundancy",
  "Sexual Abuse",
  "Stress",
  "Suicidal Thoughts",
  "Trauma",
];

// Anonymised client testimonials — attributed by first initial only to respect
// confidentiality. Jane is named in place of "a therapist" to keep the quotes
// personal. Static, so they live alongside the page copy.
const testimonials = [
  {
    quote:
      "For a long time, I tried to struggle on my own, but I wasn’t coping well. Going to therapy was one of the best decisions I have made. Working through problems with Jane’s support and experience, has helped me to understand myself more, and learn how to make changes to build a new and better life.",
    initial: "N",
  },
  {
    quote:
      "Counselling with Jane has made me become a person I would never have thought I could be.",
    initial: "J",
  },
];

function App() {
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Height of the fixed header BAR (taller on mobile due to the burger button),
  // measured live so anchored scrolls land just below it on every breakpoint.
  // Measures the bar only ("header > div"), never the open mobile dropdown menu
  // (a sibling), which would otherwise inflate the offset and stop scrolls short.
  const headerOffset = useCallback(
    () =>
      (document.querySelector("header > div")?.clientHeight ?? HEADER_OFFSET) +
      8,
    []
  );

  // Scroll a hash target below the fixed header. Uses a document-relative
  // position (rect + scrollY) so it's correct regardless of offset parents or
  // the page's top padding. Returns false if the target isn't in the DOM yet.
  const scrollToHash = useCallback(
    (hash: string): boolean => {
      const el = document.querySelector<HTMLElement>(hash);
      if (!el) return false;
      const top =
        el.getBoundingClientRect().top + window.scrollY - headerOffset();
      window.scrollTo({ top, behavior: "smooth" });
      return true;
    },
    [headerOffset]
  );

  // On arrival with a hash (e.g. returning from an article via /#blogs),
  // retry across frames until the target exists — sections like Blogs
  // mount only after their data has loaded. Keep watching after the first
  // scroll too: content mounting *above* the target (Blogs again)
  // shifts it down after the scroll has landed, so re-scroll whenever the
  // target's document-relative position drifts. User scrolling doesn't
  // change that position, so it never fights the user.
  useEffect(() => {
    if (!location.hash) return;
    let raf = 0;
    const deadline = performance.now() + HASH_WATCH_MS;
    let lastTop: number | null = null;
    const attempt = () => {
      const el = document.querySelector<HTMLElement>(location.hash);
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY;
        if (top !== lastTop) {
          lastTop = top;
          scrollToHash(location.hash);
        }
      }
      if (performance.now() < deadline) raf = requestAnimationFrame(attempt);
    };
    raf = requestAnimationFrame(attempt);
    return () => cancelAnimationFrame(raf);
  }, [location.hash, scrollToHash]);

  const handleNavigation = (url: string): void => {
    setIsDropdownOpen(false);
    scrollToHash(url);
  };

  const toggleDropdown = () => setIsDropdownOpen((prev) => !prev);

  return (
    <div className="pt-[84px] md:pt-[76px]">
      <header className="fixed top-0 inset-x-0 z-50 bg-white/85 backdrop-blur-md shadow-[0_1px_0_rgba(31,41,55,0.06)]">
        <div className="max-w-[80rem] mx-auto flex items-center justify-between px-6 md:px-9 py-5">
          {/* Wordmark */}
          <button
            onClick={() => handleNavigation("#home")}
            className="flex items-center gap-2 sm:gap-3 font-trirong_reg whitespace-nowrap text-sm sm:text-lg md:text-2xl tracking-[0.04em] sm:tracking-[0.08em] text-ink"
          >
            <LogoMark className="h-9 md:h-12 w-auto shrink-0 -translate-y-[1px]" />
            NEW DAWN COUNSELLING
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-7">
            {desktopNav.map((item) => (
              <button
                key={item.url}
                onClick={() => handleNavigation(item.url)}
                className="font-nunito font-semibold text-[13.5px] text-ink-soft hover:text-lime-700 underline-offset-4 transition-colors duration-200 hover:underline"
              >
                {item.label}
              </button>
            ))}
            <button
              onClick={() => handleNavigation("#contact")}
              className="font-nunito font-semibold text-[13.5px] text-white bg-sage hover:bg-sage-deep px-[18px] py-[9px] rounded-full transition-colors duration-200"
            >
              Get in touch
            </button>
          </nav>

          {/* Mobile burger — top right */}
          <button
            onClick={toggleDropdown}
            aria-label="Menu"
            aria-expanded={isDropdownOpen}
            className="lg:hidden flex items-center justify-center w-11 h-11 bg-transparent border-0"
          >
            <svg width="30" height="30" viewBox="0 0 24 24">
              <path
                fill="none"
                stroke="#1f2937"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                d={isDropdownOpen ? "M6 6L18 18M6 18L18 6" : "M5 7h14M5 12h14M5 17h14"}
              />
            </svg>
          </button>
        </div>

        {/* Mobile drop-down menu */}
        {isDropdownOpen && (
          <div className="lg:hidden flex flex-col bg-white/98 px-6 pb-5 pt-1">
            {mobileNav.map((item) => (
              <button
                key={item.url}
                onClick={() => handleNavigation(item.url)}
                className="font-nunito font-semibold text-[15px] text-ink-soft text-left py-[13px] px-1 border-t border-[#eef0e4]"
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
      </header>
      <div
        id="home"
        className="bg-cover object-cover flex flex-col justify-center items-center h-[300px] md:h-[700px] relative"
      >
        {/* Semi-Transparent Overlay */}
        <div className="w-full bg-white/60 py-10 backdrop-blur-[2px] md:py-16">
          <div className="mx-auto flex max-w-3xl flex-col items-center px-4 text-center">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-trirong_light font-extralight leading-snug tracking-[0.06em] text-ink">
              NEW DAWN COUNSELLING
            </h1>
            <p className="mt-4 font-nunito font-bold text-[10px] sm:text-xs md:text-sm uppercase tracking-[0.15em] sm:tracking-[0.25em] text-sage-deep">
              Humanistic counselling · Highcliffe &amp; Southampton
            </p>
          </div>
        </div>
      </div>
      <section
        id="welcome"
        className="bg-[#f9fafb] px-6 py-16 md:px-10 md:py-24"
      >
        <div className="mx-auto w-full max-w-3xl text-center space-y-6">
          <p className="font-nunito font-bold text-xs uppercase tracking-[0.2em] text-sage-deep">
            New Dawn Counselling
          </p>
          <h2 className="text-4xl md:text-5xl font-trirong_light text-ink">
            Welcome…
          </h2>
          <p className="text-base md:text-lg leading-relaxed font-trirong_light text-ink-soft">
            Welcome to New Dawn Counselling, my name is Jane. I am a humanistic
            therapist, which means that emphasis is given to the development of
            self-awareness and personal growth. Whether you are having trouble
            with general life problems, ADHD, GAD, trauma or abuse, I can offer
            adaptive, person-centred counselling to explore the best way forward
            for you.
          </p>
          <p className="text-base md:text-lg leading-relaxed font-trirong_light text-ink-soft">
            We can look at managing the symptoms of depression, anxiety, or
            overwhelm, whilst unpacking the thoughts and emotions that could be
            driving these reactions. You might choose to attend to family
            conflict or relationship difficulties, perhaps looking at past
            experiences. My job is to reflect different ways of looking at
            things, affecting a fresh outlook and change.
          </p>
          <p className="text-base md:text-lg leading-relaxed font-trirong_light text-ink-soft">
            Taking this first step can be daunting. I am sensitive to keep to
            your pace and the depth to which you would like to go, providing
            short-term focused counselling or a deeper journey to
            self-understanding.
          </p>
        </div>
      </section>
      <section
        id="about-me"
        className="bg-white px-6 py-16 md:px-10 md:py-24"
      >
        <div className="mx-auto w-full max-w-5xl space-y-8">
          <div className="space-y-4 text-center">
            <p className="font-nunito font-bold text-xs uppercase tracking-[0.2em] text-sage-deep">
              Your therapist
            </p>
            <h2 className="text-4xl md:text-5xl font-trirong_light text-ink">
              About me…
            </h2>
          </div>
          <div className="flex flex-col items-center gap-8 md:flex-row md:items-start md:gap-10">
            <img
              className="w-full rounded-2xl md:w-1/2"
              src="https://nev-web.s3.eu-west-2.amazonaws.com/new_dawn/jane.jpg"
              alt="Jane, counsellor at New Dawn Counselling"
            ></img>
            <div className="space-y-4 text-base leading-relaxed font-trirong_light text-ink-soft md:w-1/2">
              <p>
                I have always had an interest in human health in a general
                sense, and an appetite for anything of a psychological nature.
                Since qualifying with Middlesex University, I have gained my
                experience working for charities specialising in trauma and
                abuse. I have also undertaken research into burnout and
                overwhelm. I have a particular interest in relationship
                challenges, and have extensive experience around attachment
                therapy, abusive or otherwise.
              </p>
              <p>
                I am constantly developing my knowledge with continuous study
                and am a member of the Hampshire Association for Counselling and
                Psychotherapy (HACP).
              </p>
              <p>
                I am a registered member of the British Association for
                Counselling and Psychotherapy (BACP), engaging with supervision
                of my client work that is compliant with their code of ethics.
              </p>

              <p>
                I hold an enhanced DBS, and am registered with the ICO,
                complying with the Data Protection Act.
              </p>
            </div>
          </div>
        </div>
      </section>
      <section
        id="the-way-i-work"
        className="bg-[#f9fafb] px-6 py-16 md:px-10 md:py-24"
      >
        <div className="mx-auto w-full max-w-3xl text-center space-y-6">
          <p className="font-nunito font-bold text-xs uppercase tracking-[0.2em] text-sage-deep">
            My approach
          </p>
          <h2 className="text-4xl md:text-5xl font-trirong_light text-ink">
            The way I work…
          </h2>
          <p className="text-base md:text-lg leading-relaxed font-trirong_light text-ink-soft">
            My integrative approach uses Transactional Analysis to better
            understand the relationships between yourself and others — the
            dyad, and how you are influencing one another&rsquo;s responses.
          </p>
          <p className="text-base md:text-lg leading-relaxed font-trirong_light text-ink-soft">
            I also draw on psychoanalytic theory. This looks at childhood and
            past experience to identify and understand behavioural or
            relational patterns.
          </p>
          <p className="text-base md:text-lg leading-relaxed font-trirong_light text-ink-soft">
            I will share some psychological information as we go along.
            Cognitive behavioural techniques can also be helpful to ease
            physical symptoms of stress initially, such as panic attacks.
          </p>
          <div className="space-y-3 pt-6">
            <p className="font-nunito font-bold text-xs uppercase tracking-[0.2em] text-sage-deep">
              Qualifications
            </p>
            <ul className="space-y-2">
              <li className="flex items-baseline justify-center gap-2 text-base md:text-lg leading-relaxed font-trirong_light text-ink-soft">
                <span aria-hidden="true" className="text-sage">
                  &bull;
                </span>
                BSc (Hons) Humanistic Counselling with Transactional Analysis
              </li>
              <li className="flex items-baseline justify-center gap-2 text-base md:text-lg leading-relaxed font-trirong_light text-ink-soft">
                <span aria-hidden="true" className="text-sage">
                  &bull;
                </span>
                Foundation Degree in Person-Centred Counselling
              </li>
            </ul>
          </div>
        </div>
      </section>
      <section
        id="what-to-expect"
        className="bg-white px-6 py-16 md:px-10 md:py-24"
      >
        <div className="mx-auto w-full max-w-3xl space-y-10">
          <div className="space-y-6 text-center">
            <p className="font-nunito font-bold text-xs uppercase tracking-[0.2em] text-sage-deep">
              Your journey
            </p>
            <h2 className="text-4xl md:text-5xl font-trirong_light text-ink">
              What to expect…
            </h2>
            <p className="text-base md:text-lg leading-relaxed font-trirong_light text-ink-soft">
              The process is not linear or formulaic, and we work at a pace
              that is both comfortable and safe for you. The time is yours to
              talk about anything you wish. Some sessions will see you gaining
              real insights or light bulb moments. Others might be difficult or
              leave you feeling sad, but connecting with your truth is
              ultimately therapeutic.
            </p>
          </div>
          <ol className="space-y-6">
            <li className="flex items-start gap-4">
              <span
                aria-hidden="true"
                className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-sage font-nunito font-bold text-sm text-white"
              >
                1
              </span>
              <div className="space-y-1 text-base md:text-lg leading-relaxed font-trirong_light text-ink-soft">
                <p className="font-nunito font-semibold text-ink">
                  Book a free consultation
                </p>
                <p>
                  Your opportunity to tell me why you are seeking therapy, and
                  a chance for us to see what it will be like to work together.
                </p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <span
                aria-hidden="true"
                className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-sage font-nunito font-bold text-sm text-white"
              >
                2
              </span>
              <div className="space-y-1 text-base md:text-lg leading-relaxed font-trirong_light text-ink-soft">
                <p className="font-nunito font-semibold text-ink">
                  Take time to consider
                </p>
                <p>
                  You might then take some time to consider whether I am the
                  right counsellor for you.
                </p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <span
                aria-hidden="true"
                className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-sage font-nunito font-bold text-sm text-white"
              >
                3
              </span>
              <div className="space-y-1 text-base md:text-lg leading-relaxed font-trirong_light text-ink-soft">
                <p className="font-nunito font-semibold text-ink">
                  Choose a weekly slot
                </p>
                <p>
                  If we decide to continue working together, we choose a regular
                  day and time every week for 50 minutes. This secures a
                  commitment to the work and helps create a trusting
                  relationship.
                </p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <span
                aria-hidden="true"
                className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-sage font-nunito font-bold text-sm text-white"
              >
                4
              </span>
              <div className="space-y-1 text-base md:text-lg leading-relaxed font-trirong_light text-ink-soft">
                <p className="font-nunito font-semibold text-ink">
                  Your first session
                </p>
                <p>
                  Together we build a roadmap and a deeper grasp of the problem.
                </p>
              </div>
            </li>
          </ol>
        </div>
      </section>
      <section
        id="why"
        className="bg-cream px-6 py-16 md:px-10 md:py-24"
      >
        <div className="mx-auto w-full max-w-3xl text-center space-y-6">
          <p className="font-nunito font-bold text-xs uppercase tracking-[0.2em] text-sage-deep">
            A fresh perspective
          </p>
          <h2 className="text-4xl md:text-5xl font-trirong_light text-ink">
            Why have counselling?…
          </h2>
          <p className="text-base md:text-lg leading-relaxed font-trirong_light text-ink-soft">
            When we have a problem, we can become stuck in it and caught up in
            repetitive ways of thinking, coming to the same conclusions each
            time. I am trained to go beneath the surface of the problem, helping
            you to gain a different response.
          </p>
          <p className="text-base md:text-lg leading-relaxed font-trirong_light text-ink-soft">
            This new perspective can be hard to reach with anyone that has an
            emotional connection to us. This could be due to the holding of your
            distress. They might also give unwanted advice and answers drawn out
            of their own life experience, or bias. Often we just keep our
            emotions a secret due to feelings of being faulty. A non-judgemental
            environment aids the removal of shame, providing a safe space to
            explore yourself.{" "}
          </p>
        </div>
      </section>
      <section
        id="services"
        className="bg-[#f9fafb] px-6 py-16 md:px-10 md:py-24"
      >
        <div className="mx-auto w-full max-w-5xl space-y-10">
          <div className="space-y-6 text-center">
            <p className="font-nunito font-bold text-xs uppercase tracking-[0.2em] text-sage-deep">
              How we can work together
            </p>
            <h2 className="text-4xl md:text-5xl font-trirong_light text-ink">
              My services…
            </h2>
            <div className="mx-auto max-w-2xl space-y-1">
              <p className="text-base md:text-lg leading-relaxed font-trirong_light text-ink-soft">
                I offer sessions online and face-to-face in Highcliffe or Southampton. I cover surrounding areas such as Lymington, New Milton, Christchurch, Southbourne and Pennington.
              </p>
              <p className="font-nunito font-semibold text-sm text-ink-soft">Monday – Saturday</p>
              <p className="font-nunito font-semibold text-sm text-ink-soft">
                Evening appointments available
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-8">
            <div className="flex flex-col items-center space-y-4 rounded-2xl bg-white p-8 text-center shadow-card">
              <img
                className="h-32 w-32 rounded-full object-cover"
                src="https://nev-web.s3.eu-west-2.amazonaws.com/new_dawn/Image-by-Arnaud-Mesureur.jpg"
                alt="forest-tree"
              ></img>
              <h3 className="text-2xl font-trirong_reg text-ink">
                Consultation
              </h3>
              <p className="text-sm leading-relaxed font-trirong_light text-ink-soft">
                We will have a chat over the phone or Google Meet initially. This will take
                about 20 minutes and will help you to decide if I am the right
                fit for you. There is no charge for this.
              </p>
            </div>
            <div className="flex flex-col items-center space-y-4 rounded-2xl bg-white p-8 text-center shadow-card">
              <img
                className="h-32 w-32 rounded-full object-cover"
                src="https://nev-web.s3.eu-west-2.amazonaws.com/new_dawn/Forest-Trees.jpg"
                alt="forest-tree"
              ></img>
              <h3 className="text-2xl font-trirong_reg text-ink">
                Individual counselling
              </h3>
              <p className="text-sm leading-relaxed font-trirong_light text-ink-soft">
                Sessions will then take place, on a weekly basis initially. You
                might then choose to progress to fortnightly or monthly
                sessions, at a later date. Each session is 50 minutes long, at a
                cost of £60.
              </p>
              <p className="text-sm leading-relaxed font-trirong_light text-ink-soft">
                I can offer single-session counselling for £70 for 60 minutes.
              </p>
            </div>
            <div className="flex flex-col items-center space-y-4 rounded-2xl bg-white p-8 text-center shadow-card">
              <img
                className="h-32 w-32 rounded-full object-cover"
                src="https://nev-web.s3.eu-west-2.amazonaws.com/new_dawn/Trees.jpg"
                alt="forest-tree"
              ></img>
              <h3 className="text-2xl font-trirong_reg text-ink">
                Couples counselling
              </h3>
              <p className="text-sm leading-relaxed font-trirong_light text-ink-soft">
                Couples counselling will also take place on a weekly basis, at a
                cost of £90 for 60 minutes.
              </p>
            </div>
          </div>
        </div>
      </section>
      <section
        id="help"
        className="bg-white px-6 py-16 md:px-10 md:py-24"
      >
        <div className="mx-auto w-full max-w-5xl space-y-10 rounded-2xl bg-[#f9fafb] p-6 shadow-card md:p-10">
          <div className="space-y-4 text-center">
            <p className="font-nunito font-bold text-xs uppercase tracking-[0.2em] text-sage-deep">
              Areas of support
            </p>
            <h2 className="text-4xl md:text-5xl font-trirong_light text-ink">
              I can help with…
            </h2>
          </div>
          <div className="flex flex-col items-center gap-8 lg:flex-row lg:items-start">
            <img
              className="w-full rounded-2xl object-cover lg:w-[500px]"
              src="https://nev-web.s3.eu-west-2.amazonaws.com/new_dawn/bcad7d3a-f0a3-4977-bf18-720dcf4a73f6+2.jpg"
              alt="help"
            ></img>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 sm:text-xs md:text-base">
              {areas_of_support.map((item) => (
                <p className="text-left font-trirong_light text-ink-soft" key={item}>
                  {item}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>
      <Blogs />
      <section
        id="testimonials"
        className="bg-cream px-6 py-16 md:px-10 md:py-24"
      >
        <div className="mx-auto w-full max-w-5xl">
          <div className="space-y-4 text-center">
            <h2 className="text-4xl md:text-5xl font-trirong_light text-ink">
              Testimonials…
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 items-stretch gap-6 md:mt-16 md:grid-cols-2 md:gap-8">
            {testimonials.map(({ quote, initial }) => (
              <figure
                key={initial}
                className="flex h-full flex-col rounded-2xl bg-white p-8 shadow-card md:p-10"
              >
                <span
                  aria-hidden="true"
                  className="select-none font-trirong_reg text-6xl leading-[0.5] text-sage/40"
                >
                  &ldquo;
                </span>
                <blockquote className="mt-5 flex-1 text-base md:text-lg leading-relaxed font-trirong_light italic text-ink-soft">
                  {quote}
                </blockquote>
                {/* Circular sage monogram — the client's first initial, kept
                    anonymous but given the weight of a signature. */}
                <figcaption
                  data-testid="testimonial-initial"
                  className="mt-7 flex items-center gap-3 border-t border-hairline pt-6"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-sage font-trirong_reg text-xl text-white">
                    {initial}
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>
      <Faq onNavigate={handleNavigation} />
      <Contact />
      <footer className="border-t border-hairline bg-[#f9fafb] px-6 py-16 md:px-10">
        <div className="mx-auto flex max-w-5xl flex-col items-center space-y-4 text-center">
          <p className="font-trirong_reg text-xl tracking-[0.08em] text-ink">New Dawn Counselling</p>
          <a
            href="mailto:newdawncounselling.info@gmail.com"
            className="font-nunito text-sm text-ink-soft transition-colors hover:text-sage-deep"
          >
            newdawncounselling.info@gmail.com
          </a>
          <a
            href="https://www.facebook.com/profile.php?id=61579957202257"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Facebook"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="#4267B2">
              <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/>
            </svg>
          </a>
        </div>
      </footer>
    </div>
  );
}

export default App;
